import React, { ButtonHTMLAttributes, ElementType, forwardRef } from 'react';
import { Link } from 'react-router-dom';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * The element or component to render as
   * - Defaults to 'button'
   * - Can be any valid HTML element or React component (e.g., Menu.Button from headlessui)
   */
  as?: ElementType;

  /**
   * If provided, renders as a React Router Link instead of a button
   * This enables proper navigation UX (right-click to open in new tab, URL preview on hover, etc.)
   * @example
   * <Button variant="primary" href="/trips/123">View Trip</Button>
   */
  href?: string;

  /**
   * Button visual variant
   * - primary: Main action button with brand colors
   * - secondary: Alternative action with subtle styling
   * - destructive: For dangerous actions (delete, remove, etc.)
   * - ghost: Minimal styling, text-only appearance
   * - outline: Bordered button with transparent background
   * - outline-blurred: Bordered button with dark, blurred, half-transparent background
   */
  variant?: 'primary' | 'secondary' | 'destructive' | 'ghost' | 'outline' | 'outline-blurred';

  /**
   * Button size
   */
  size?: 'sm' | 'md' | 'lg';

  /**
   * Whether the button is in a loading state
   */
  loading?: boolean;

  /**
   * Optional icon to display before the button text
   */
  iconBefore?: React.ReactNode;

  /**
   * Optional icon to display after the button text
   */
  iconAfter?: React.ReactNode;

  /**
   * If true, button will take full width of container
   */
  fullWidth?: boolean;

  /**
   * If true, keeps the branded color even when disabled (instead of showing gray)
   * Useful for CTA buttons where you want to maintain brand visibility
   */
  keepColorWhenDisabled?: boolean;
}

/**
 * Button component with semantic UI tokens
 *
 * @example
 * // Primary button
 * <Button variant="primary" onClick={handleSave}>
 *   Save Changes
 * </Button>
 *
 * @example
 * // Loading state
 * <Button variant="primary" loading disabled>
 *   Saving...
 * </Button>
 *
 * @example
 * // With icons
 * <Button variant="destructive" iconBefore={<TrashIcon className="w-4 h-4" />}>
 *   Delete
 * </Button>
 *
 * @example
 * // As a Menu.Button from headlessui
 * <Menu.Button as={Button} variant="secondary" size="sm">
 *   Options
 * </Menu.Button>
 *
 * @example
 * // As a Link for navigation
 * <Button variant="primary" href="/trips/123">
 *   View Trip
 * </Button>
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      as: Component = 'button',
      href,
      variant = 'primary',
      size = 'md',
      loading = false,
      iconBefore,
      iconAfter,
      fullWidth = false,
      keepColorWhenDisabled = false,
      className = '',
      children,
      disabled,
      ...props
    },
    ref,
  ) => {
    // If href is provided, render as a Link component for proper navigation
    const ActualComponent = href ? Link : Component;

    // Link uses 'to' prop instead of 'href'
    const linkProps = href ? { to: href } : {};

    // Base styles applied to all buttons
    const baseStyles = `
      inline-flex items-center justify-center gap-2
       transition-all duration-200
      focus:outline-none focus:ring-2 focus:ring-offset-2
      disabled:cursor-not-allowed
    `;

    // Variant-specific styles using semantic tokens
    const variantStyles: Record<NonNullable<ButtonProps['variant']>, string> = {
      primary: `
        bg-action-primary text-on-brand
        hover:bg-action-primary-hover
        focus:ring-brand
        ${keepColorWhenDisabled ? 'disabled:bg-action-primary disabled:text-on-brand disabled:opacity-90' : 'disabled:bg-muted disabled:text-disabled'}
        shadow-md hover:shadow-lg
        transform hover:scale-105 disabled:transform-none disabled:hover:scale-100
      `,
      secondary: `
        bg-surface text-main
        border-2 border-default
        hover:bg-subtle hover:border-brand
        focus:ring-brand
        disabled:bg-muted disabled:text-disabled disabled:border-subtle
        shadow-sm hover:shadow-md
      `,
      destructive: `
        bg-error-subtle text-error
        border-2 border-error
        hover:bg-error-subtle hover:border-error
        focus:ring-error
        disabled:bg-muted disabled:text-disabled disabled:border-subtle
        shadow-sm hover:shadow-md
      `,
      ghost: `
        bg-transparent text-main
        hover:bg-subtle
        focus:ring-brand
        disabled:text-disabled disabled:hover:bg-transparent
      `,
      outline: `
        bg-transparent text-brand
        border-2 border-brand
        hover:bg-action-primary hover:text-on-brand
        focus:ring-brand
        disabled:text-disabled disabled:border-subtle disabled:hover:bg-transparent
      `,
      'outline-blurred': `
        bg-neutral-darkest/20 text-on-dark
        border-2 border-dark
        hover:bg-neutral-darkest/30 hover:text-on-brand
        backdrop-blur-2xl
        focus:ring-brand
        disabled:text-disabled disabled:border-subtle disabled:hover:bg-transparent
      `,
    };

    // Size-specific styles
    const sizeStyles = {
      sm: 'px-3 py-1.5 text-xs rounded-lg',
      md: 'px-4 py-2.5 text-sm rounded-xl',
      lg: 'px-6 py-3 text-base rounded-xl',
    };

    // Width style
    const widthStyle = fullWidth ? 'w-full' : '';

    // Combine all styles
    const combinedClassName = `
      ${baseStyles}
      ${variantStyles[variant]}
      ${sizeStyles[size]}
      ${widthStyle}
      ${className}
    `
      .trim()
      .replace(/\s+/g, ' ');

    // When rendering as Link, we shouldn't apply disabled attribute
    // Instead we'll handle it with styling and pointer-events
    const shouldDisable = (disabled || loading) && !href;

    // Loading spinner component
    const Spinner = () => (
      <svg
        className="animate-spin h-4 w-4"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    );

    const content = (
      <>
        {loading && <Spinner />}
        {!loading && iconBefore && <span className="inline-flex">{iconBefore}</span>}
        {children}
        {!loading && iconAfter && <span className="inline-flex">{iconAfter}</span>}
      </>
    );

    return React.createElement(
      ActualComponent,
      {
        ref,
        className: combinedClassName,
        disabled: shouldDisable,
        ...linkProps,
        ...props,
      },
      content,
    );
  },
);

Button.displayName = 'Button';
